import { isEmailAddress } from '../validators';
import { normalizeEmail } from './inputRules';
import type { StringKey } from '../strings';

/**
 * 🔒 A company's mailbox that is no person's (piece 10; decision 2971; T2052):
 * one dated row per address, such as its sales or its accounts receivable box.
 * The Company page's Mailboxes tile adds, corrects and ends a row (decision
 * 2972), the import's one-name window writes one (decision 2973), and the send
 * windows read the box of the document's kind ahead of a named contact
 * (decision 2974).
 */
export const COMPANY_MAILBOX_KINDS = ['Sales', 'AccountsReceivable', 'AccountsPayable', 'Support', 'General', 'Other'] as const;
export type CompanyMailboxKindValue = (typeof COMPANY_MAILBOX_KINDS)[number];

/** Each kind's name as a person reads it. */
export const COMPANY_MAILBOX_KIND_LABEL_KEYS: Readonly<Record<CompanyMailboxKindValue, StringKey>> = {
  Sales: 'mailbox.kind.sales',
  AccountsReceivable: 'mailbox.kind.accountsReceivable',
  AccountsPayable: 'mailbox.kind.accountsPayable',
  Support: 'mailbox.kind.support',
  General: 'mailbox.kind.general',
  Other: 'mailbox.kind.other',
};

export function isCompanyMailboxKind(value: unknown): value is CompanyMailboxKindValue {
  return typeof value === 'string' && (COMPANY_MAILBOX_KINDS as readonly string[]).includes(value);
}

/** One mailbox row as the tile and the windows read it. Dates are calendar days. */
export interface CompanyMailboxRow {
  id: string;
  version: number;
  companyId: string;
  kind: CompanyMailboxKindValue;
  label: string | null;
  email: string;
  startDate: string;
  endDate: string | null;
}

/**
 * 🔒 The box a send window opens To on (decision 2974): the document's answer
 * carries it, chosen by the server on the Organization's own day, so the window
 * lists no mailboxes and works out no day of its own, and a person who cannot
 * read companies is told the address the mail will go to all the same.
 */
export interface SendMailbox {
  kind: CompanyMailboxKindValue;
  email: string;
}

/** What the tile's window sends: the kind, the row's own word for the box, and the address. */
export interface CompanyMailboxDraft {
  kind: CompanyMailboxKindValue;
  label?: string | null;
  email: string;
}

/** The refusal a draft meets, or null: a kind of the six and an address of the one shape. */
export function companyMailboxDraftProblem(draft: { kind?: unknown; email?: unknown }): string | null {
  if (!isCompanyMailboxKind(draft.kind)) return 'Choose what the mailbox is for.';
  if (typeof draft.email !== 'string' || !isEmailAddress(draft.email)) return 'Type the mailbox’s email address.';
  return null;
}

/** The address as it is stored, folded as a work email is. */
export function mailboxEmail(value: string): string {
  return normalizeEmail(value);
}

/** Whether a row is in force on a calendar day: started by it and ending on it or after (piece 10; decision 2860). */
export function mailboxInForce(row: { startDate: string; endDate: string | null }, day: string): boolean {
  return row.startDate <= day && (row.endDate === null || row.endDate >= day);
}

/**
 * 🔒 The box a document goes to ahead of a named contact (decision 2974): the
 * first open row of the first kind named, oldest first, or null.
 */
export function mailboxOfKinds<T extends { kind: CompanyMailboxKindValue; startDate: string; endDate: string | null }>(rows: readonly T[], kinds: readonly CompanyMailboxKindValue[], day: string): T | null {
  for (const kind of kinds) {
    const found = rows.filter((row) => row.kind === kind && mailboxInForce(row, day)).sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
    if (found) return found;
  }
  return null;
}

/** 🔒 The kinds each send reads (decision 2974): an invoice the receivable box, a sales order the payable box, an RFQ the vendor's sales box. */
export const SEND_MAILBOX_KINDS = {
  Invoice: ['AccountsReceivable'],
  SalesOrder: ['AccountsPayable'],
  RFQ: ['Sales'],
} as const satisfies Readonly<Record<string, readonly CompanyMailboxKindValue[]>>;

/** The company's mailboxes, open and ended, and the add route. */
export function companyMailboxesPath(companyId: string): string {
  return `/company-mailboxes/company/${encodeURIComponent(companyId)}`;
}
/** One mailbox: its correction. */
export function companyMailboxPath(mailboxId: string): string {
  return `/company-mailboxes/${encodeURIComponent(mailboxId)}`;
}
/** One mailbox's End, which sets its end date to today. */
export function companyMailboxEndPath(mailboxId: string): string {
  return `${companyMailboxPath(mailboxId)}/end`;
}
